import { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectBuildDetails } from '@pages/stands/store/selectors';
import DeployFilters from './DeployFilters';
import { DeployFiltersError, SelectedFilter } from '@shared/types';

interface DeployFirstStepProps {
    isCurrentStep: boolean;
    selectedFilter: SelectedFilter;
    setSelectedFilter: Dispatch<SetStateAction<SelectedFilter>>;
    mainModalOpen: boolean;
    error: DeployFiltersError;
    shouldRegisterNode: boolean;
    setShouldRegisterNode: Dispatch<SetStateAction<boolean>>;
    shouldActivateLicense: boolean;
    setShouldActivateLicense: Dispatch<SetStateAction<boolean>>;
    shouldEnableRootAccess: boolean;
    setShouldEnableRootAccess: Dispatch<SetStateAction<boolean>>;
    showProductConfiguration?: boolean;
}

const DeployFirstStep: FC<DeployFirstStepProps> = ({
    isCurrentStep,
    selectedFilter,
    setSelectedFilter,
    error,
    shouldRegisterNode,
    setShouldRegisterNode,
    shouldActivateLicense,
    setShouldActivateLicense,
    shouldEnableRootAccess,
    setShouldEnableRootAccess,
}) => {
    const { t } = useTranslation();
    const buildDetails: SelectedFilter['build_details'][] = useSelector(selectBuildDetails);

    const handleShouldRegisterNodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;

        if (!newValue) {
            setShouldActivateLicense(false);
            setShouldEnableRootAccess(false);
        }

        setShouldRegisterNode(newValue);
    };

    const handleSecondaryCheckboxChange =
        (setState: Dispatch<SetStateAction<boolean>>) => (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.checked;

            if (newValue) {
                setShouldRegisterNode(true);
            }

            setState(newValue);
        };

    const handleShouldActivateLicenseChange = handleSecondaryCheckboxChange(setShouldActivateLicense);

    const handleShouldEnableRootAccessChange = handleSecondaryCheckboxChange(setShouldEnableRootAccess);

    return (
        <Box
            sx={{
                display: isCurrentStep ? 'flex' : 'none',
                flexDirection: 'column',
                gap: 2,
                pb: 2,
                alignItems: 'flex-start',
            }}
        >
            <DeployFilters
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                error={error}
                disabledBuild={!selectedFilter.branch}
                buildDetails={buildDetails}
                showArchitecture={true}
                showIsPublic={true}
            />
            <Divider flexItem />
            <FormControl sx={{ mt: 1 }} component="fieldset" variant="standard">
                <FormLabel component="legend">{t('product_configuration')}</FormLabel>
                <FormGroup
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    <FormControlLabel
                        control={<Checkbox checked={shouldRegisterNode} onChange={handleShouldRegisterNodeChange} />}
                        label={t('register_nodes')}
                    />
                    <FormControlLabel
                        disabled={!shouldRegisterNode}
                        control={
                            <Checkbox checked={shouldActivateLicense} onChange={handleShouldActivateLicenseChange} />
                        }
                        label={t('activate_license')}
                    />
                    <FormControlLabel
                        disabled={!shouldRegisterNode}
                        control={
                            <Checkbox checked={shouldEnableRootAccess} onChange={handleShouldEnableRootAccessChange} />
                        }
                        label={t('should_enable_root_ssh')}
                    />
                </FormGroup>
            </FormControl>
        </Box>
    );
};

export default DeployFirstStep;
